import type { ColumnsType } from 'antd/es/table';
import {
  Box,
  Colors,
  IcClose,
  IcDevice,
  IcEdit,
  IcMenuChange,
  IcTick,
  IcTrash,
  Pagination,
  Table,
  Text,
  StatusTag,
  IcCopy,
} from 'basic-ui-lib';
import EditNameModal, { EditNameModalRef } from 'basic-ui-lib/components/Modal/EditNameModal';
import { ActionProps } from 'basic-ui-lib/components/Table/Table';
import { IScreen } from 'modules/dashboard/interfaces/api';
import dashboardApis from 'modules/dashboard/services/apis';
import { ScreenStatus, StatusScreenConfigs } from 'modules/dashboard/services/constants';
import useGetListScreens from 'modules/dashboard/services/hooks/useGetListScreens';
import { useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';
import { getMessageError } from 'services/response';
import styled from 'styled-components';
import toast from 'utils/toast';
import ModalConfirm, { useModalConfirmRef } from 'widgets/ModalConfirm';
import { setPageLoading } from 'widgets/PageLoading';
import ScreenTableMobile from './ScreenTableMobile';
interface IScreenTableProps {
  id?: string;
}

const ScreenTable = ({ id }: IScreenTableProps) => {
  const modalConfirmRef = useModalConfirmRef();
  const navigate = useNavigate();
  const { screensList, screensParams, mutate } = useGetListScreens(true);
  const editNameModalRef = useRef<EditNameModalRef>(null);
  const isMobile = useMediaQuery({ maxWidth: 768 });

  useEffect(() => {
    if (id) {
      mutate({ page: 1, size: 10, storeId: id });
      return;
    }

    mutate({ page: 1, size: 10 });

  }, [id]);

  const columns: ColumnsType<IScreen> = [
    {
      title: 'STORE NAME',
      dataIndex: 'businessName',
      render: (text, record) => (
        <button onClick={handleDetailStore(record?.storeId)}>
          <Text content_2 medium color='primary.6'>
            {text}
          </Text>
        </button>
      ),
    },
    {
      title: 'SCREEN NAME',
      dataIndex: 'playerName',
    },
    {
      title: 'PLAYLIST NAME',
      dataIndex: 'playListName',
      render: (text, record) => (
        <button onClick={handleDetailPlaylist(record.playListId)}>
          <Text body_1 medium color='primary.6'>
            {text}
          </Text>
        </button>
      ),
    },
    {
      title: 'PAIRING CODE',
      dataIndex: 'code',
      render: (text) => (
          <Box width='80px' display='flex' justifyContent='center' alignItems='center' gap={4} backgroundColor={Colors.orange[2]} borderRadius={100} padding='2px 0'>
            <Text body_1 medium color='neutral.text.1'>
              {text}
            </Text>
          </Box>
        ),
      },
    {
      title: 'STATUS',
      dataIndex: 'status',
      render: (status) => {
        const config = StatusScreenConfigs[status as ScreenStatus];
        return <StatusTag label={config.label} backgroundColor={config.backgroundColor} color={config.color} />;
      }
    },
    // {
    //   title: 'DEVICE NAME',
    //   dataIndex: 'deviceName',
    // },
    {
      title: 'DEVICE TYPE',
      dataIndex: 'deviceType',
    },
    {
      title: 'OPERATION',
      dataIndex: 'operatingSystem',
    },
    {
      title: 'LAST ACTIVE',
      dataIndex: 'lastTimeActive',
    },
  ];

  const actions: ActionProps<IScreen>[] = [
    {
      label: 'Refresh',
      icon: <IcMenuChange width={16} height={16} />,
      onClick: (item) => {
        handleRefreshScreen(item.id);
      },
    },
    {
      label: 'Preview',
      icon: <IcCopy width={16} height={16} />,
      onClick: (item) => {
        handlePreviewScreen(item);
      },
    },
    {
      label: 'Edit Name',
      icon: <IcEdit width={16} height={16} />,
      onClick: (item) => {
        editNameModalRef.current?.open({
          currentName: item.playerName,
          id: item.id,
          onFinish: handleUpdateName,
          modalTitle: 'Edit Screen Name',
        });
      },
    },
    {
      label: 'Edit Content',
      icon: <IcDevice width={16} height={16} />,
      onClick: (item) => {
        navigate(`/screen/${item.id}`);
      },
    },
    {
      label: (item) => (item.status === 'ONLINE' ? 'Deactivate' : 'Reactivate'),
      icon: (item) =>
        item.status === 'ONLINE' ? <IcClose width={16} height={16} /> : <IcTick width={16} height={16} />,
      onClick: (item) => {
        handleChangeStatus(item);
      },
      hidden: (item) => item.status === 'DRAFT',
    },
    {
      label: 'Delete Screen',
      icon: <IcTrash width={16} height={16} />,
      onClick: (item) => {
        onDeleteScreen(item.id);
      },
    },
  ];

  const handlePageChange = (page: number) => {
    mutate({ ...screensParams, page });
  };

  const handleRefreshScreen = async (screenId: string) => {
    try {
      setPageLoading(true);
      const res = await dashboardApis.refreshScreen(screenId);

      if(res?.data?.data) {
        toast.success('Screen refreshed successfully');
        mutate({
          ...screensParams,
        });
      }
    } catch (error) {
      toast.error(getMessageError(error));
    } finally {
      setPageLoading(false);
    }
  };

  const handleDeleteScreen = async (screenId: string) => {
    try {
      setPageLoading(true);
      const res = await dashboardApis.deleteScreen(screenId);
      if(res?.data?.data) {
        toast.success('Screen deleted successfully');
        mutate({
          ...screensParams,
        });
      }
    } catch (error) {
      toast.error(getMessageError(error));
    }
    finally {
      setPageLoading(false);
    }
  };

  const onDeleteScreen = (screenId: string) => {
    modalConfirmRef.current?.open(() => handleDeleteScreen(screenId), {
      message: isMobile ? 'Do you want to delete this screen?' : 'Are you sure you want to delete this screen? This action cannot be undone.',
    });
  };

  const handleChangeStatus = async (item: IScreen) => {
    try {
      
      if(item.status === ScreenStatus.ONLINE) {
        const res = await dashboardApis.updateStatusScreen({id: item.id, status: ScreenStatus.DEACTIVATED});

        if(res?.data?.data) {
          toast.success('Screen deactivated successfully');
          mutate({
            ...screensParams,
          });
        }
      } else {
        const res = await dashboardApis.updateStatusScreen({id: item.id, status: ScreenStatus.ONLINE});
        if(res?.data?.data) {
          toast.success('Screen reactivated successfully');
          mutate({
            ...screensParams,
          });
        }
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'An error occurred while updating screen status');
    }
  };

  const handleUpdateName = async (id: string, newName: string) => {
    try {
      await dashboardApis.updateNameScreen({ screenId: id, name: newName });
      toast.success('Name updated successfully');
      mutate({
        ...screensParams,
      });
    } catch (error) {
      toast.error(getMessageError(error) || 'An error occurred while updating the name');
    }
  };

  const handleDetailPlaylist = (playlistId: string) => () => {
    navigate(`/playlist/${playlistId}`);
  };

  const handleDetailStore = (storeId: string | null) => () => {
    if (!storeId) {
      return;
    }
    navigate(`/store/${storeId}`);
  };

  const handlePreviewScreen = (item: IScreen) => {
    window.open(
      `/preview/${item.id}`,
      '_blank',
    );
  };

  if (isMobile) {
    return (
      <>
        <ScreenTableMobile 
          handleRefreshScreen={handleRefreshScreen}
          handlePreviewScreen={handlePreviewScreen}
          handleEditNameScreen={(item) => editNameModalRef.current?.open({
            currentName: item.playerName,
            id: item.id,
            onFinish: handleUpdateName,
            modalTitle: 'Edit Screen Name',
          })}
          handleReactivateScreen={handleChangeStatus}
          handleDeleteScreen={onDeleteScreen}
        />
        <ModalConfirm ref={modalConfirmRef} />
        <EditNameModal ref={editNameModalRef} />
      </>
    );
  }

  return (
    <TableBox>
      <Table<IScreen>
        className='custom-table'
        columns={columns}
        dataSource={screensList?.content || []}
        scroll={{ x: 'max-content' }}
        pagination={false}
        actions={actions}
      />
      <Pagination
        totalItems={screensList?.totalElements || 0}
        itemsPerPage={screensParams.size || 10}
        currentPage={screensParams.page || 1}
        onPageChange={handlePageChange}
        labelDisplay='screens'
        showRowPerPage={true}
        maxVisiblePages={3}
      />
      <ModalConfirm ref={modalConfirmRef} />
      <EditNameModal ref={editNameModalRef} />
    </TableBox>
  );
};

export default ScreenTable;

const TableBox = styled.div`
  width: 100%;
`;
